import { InferType, object, string, boolean } from "yup";
import { useMemo } from "react";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import VariableInputField from "./shared/VariableInputField";
import { useEntities } from "@/hooks/useCommonQueries";
import CheckboxField from "@/components/form/CheckboxField";
import RlsUserGroupFields, {
  rlsUserGroupSchemaFields,
} from "./shared/RlsUserGroupFields";
import { useFormikContext } from "formik";
import PayloadFieldMapper from "./shared/PayloadFieldMapper";
import { WorkflowStep } from "@/types/breeze/Workflow";

export const createDataValidationSchema = object({
  parameters: object({
    entity_name: string().required("Entity name is required"),
    payload: string().required("Payload is required"),
    prevent_duplicates: boolean(),
    auto_set_rls: boolean(),
    rls_user_field: string(),
    auto_set_rls_groups: boolean(),
    rls_group_field: string(),
    ...rlsUserGroupSchemaFields,
    loop_over: string(),
  }),
});

export type CreateDataFormValues = InferType<typeof createDataValidationSchema>;

interface CreateDataFieldsProps {
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

export default function CreateDataFields({
  existingSteps = [],
  triggerEntity,
}: CreateDataFieldsProps) {
  const { data: entities } = useEntities();
  const { values } = useFormikContext<CreateDataFormValues>();
  const selectedEntityName = values?.parameters?.entity_name;

  const entityOptions = useMemo(
    () => entities?.map((e) => ({ value: e.name, label: e.name })) || [],
    [entities],
  );

  return (
    <div>
      <SearchableSelectField
        name="parameters.entity_name"
        label="Entity"
        required
        options={entityOptions}
        searchPlaceholder="Search entities..."
        placeholder="Select entity..."
      />

      <VariableInputField
        name="parameters.loop_over"
        label="Loop Over Array"
        placeholder="{{ $anythink.steps.api_call.data }}"
        description="Optional: Reference a previous step's array output to loop through. If set, this step runs for each item, and you can map fields below using local item properties (e.g. {{ name }})."
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
      />

      <PayloadFieldMapper
        name="parameters.payload"
        entityName={selectedEntityName || ""}
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
        label="Payload"
        required
      />

      <CheckboxField
        name="parameters.prevent_duplicates"
        label="Prevent Duplicates"
      />

      <CheckboxField
        name="parameters.auto_set_rls"
        label="Auto-set RLS"
        description="Automatically grant row-level security access to the user specified in the payload"
      />

      <SearchableSelectField
        name="parameters.rls_user_field"
        label="RLS User Field"
        options={[
          { value: "user", label: "user" },
          { value: "user_id", label: "user_id" },
          { value: "owner", label: "owner" },
          { value: "owner_id", label: "owner_id" },
          { value: "created_by", label: "created_by" },
        ]}
        variableToggle
        placeholder="user (default)"
        description="The field name in the payload that contains the user ID for RLS"
      />

      <CheckboxField
        name="parameters.auto_set_rls_groups"
        label="Auto-set RLS Groups"
        description="Automatically grant row-level security access to the group specified in the payload"
      />

      <SearchableSelectField
        name="parameters.rls_group_field"
        label="RLS Group Field"
        options={[
          { value: "group", label: "group" },
          { value: "group_id", label: "group_id" },
          { value: "team", label: "team" },
          { value: "team_id", label: "team_id" },
        ]}
        variableToggle
        placeholder="group (default)"
        description="The field name in the payload that contains the group ID for RLS"
      />

      <RlsUserGroupFields />
    </div>
  );
}
